import type { GarmentAttributeCatalog, GarmentAttributeType } from '../interfaces/Company';

export const GARMENT_ATTRIBUTE_TYPES: GarmentAttributeType[] = ['color', 'damage', 'stain', 'brand'];

export const FRONTEND_FALLBACK_GARMENT_ATTRIBUTE_CATALOGS: readonly GarmentAttributeCatalog[] = [
  {
    type: 'color',
    values: [
      'Rojo',
      'Azul',
      'Verde',
      'Amarillo',
      'Naranja',
      'Morado',
      'Rosa',
      'Negro',
      'Blanco',
      'Gris',
      'Marrón',
      'Turquesa',
      'Beige',
      'Violeta',
      'Celeste',
      'Dorado',
      'Plateado',
      'Coral',
      'Lavanda',
      'Fucsia',
    ],
  },
  {
    type: 'damage',
    values: [
      'Rasgado',
      'Manchado',
      'Descolorido',
      'Quemado',
      'Encogido',
      'Estirado',
      'Pelusa',
      'Rotura de costura',
      'Agujero',
      'Desgaste en cuello',
      'Desgaste en puños',
      'Daño por plancha',
      'Pérdida de botones',
      'Cremallera rota',
      'Tela deshilachada',
      'Marcas de pegamento',
      'Desgaste general',
      'Arrugas profundas',
      'Daño por humedad',
      'Daño por moho',
    ],
  },
  {
    type: 'stain',
    values: [
      'Aceite',
      'Grasa',
      'Vino tinto',
      'Café',
      'Tinta',
      'Sangre',
      'Chocolate',
      'Sudor',
      'Lodo',
      'Hierba',
      'Frutas',
      'Salsas',
      'Lápiz labial',
      'Pintura',
      'Cerveza',
      'Té',
      'Moho',
      'Adhesivo',
      'Resina',
      'Óxido',
    ],
  },
  {
    type: 'brand',
    values: [
      'Nike',
      'Adidas',
      'Levis',
      'Zara',
      'H&M',
      'Gucci',
      'Prada',
      'Louis Vuitton',
      'Under Armour',
      'Calvin Klein',
      'Ralph Lauren',
      'Tommy Hilfiger',
      'The North Face',
      'Patagonia',
      'Columbia',
      'Lacoste',
      'Puma',
      'Forever 21',
      'Uniqlo',
      'Victorias Secret',
      'Old Navy',
      'Gap',
      'Versace',
      'Burberry',
      'Balenciaga',
      'Fendi',
      'Dolce & Gabbana',
      'Supreme',
      'Off-White',
      'Hollister',
    ],
  },
];

export function getFrontendFallbackGarmentAttributeCatalogs(): GarmentAttributeCatalog[] {
  return FRONTEND_FALLBACK_GARMENT_ATTRIBUTE_CATALOGS.map((catalog) => ({
    type: catalog.type,
    values: [...catalog.values],
  }));
}

function isValidType(value: unknown): value is GarmentAttributeType {
  return typeof value === 'string' && GARMENT_ATTRIBUTE_TYPES.includes(value as GarmentAttributeType);
}

export function resolveGarmentAttributeCatalogs(input: unknown): GarmentAttributeCatalog[] {
  if (!Array.isArray(input)) {
    return getFrontendFallbackGarmentAttributeCatalogs();
  }

  const byType = new Map<GarmentAttributeType, string[]>();

  for (const entry of input) {
    if (!entry || typeof entry !== 'object' || Array.isArray(entry)) {
      return getFrontendFallbackGarmentAttributeCatalogs();
    }

    const { type, values } = entry as { type?: unknown; values?: unknown };
    if (!isValidType(type) || !Array.isArray(values) || byType.has(type)) {
      return getFrontendFallbackGarmentAttributeCatalogs();
    }

    if (!values.every((value) => typeof value === 'string')) {
      return getFrontendFallbackGarmentAttributeCatalogs();
    }

    byType.set(type, [...values]);
  }

  if (byType.size !== GARMENT_ATTRIBUTE_TYPES.length) {
    return getFrontendFallbackGarmentAttributeCatalogs();
  }

  return GARMENT_ATTRIBUTE_TYPES.map((type) => ({
    type,
    values: [...(byType.get(type) || [])],
  }));
}

export function getGarmentAttributeCatalogValues(
  catalogs: GarmentAttributeCatalog[] | null | undefined,
  type: GarmentAttributeType,
): string[] {
  const resolvedCatalogs = resolveGarmentAttributeCatalogs(catalogs);
  return resolvedCatalogs.find((catalog) => catalog.type === type)?.values || [];
}
